import React from 'react';
import { trpc } from '@org/clients/school';
import { GenericModal } from '@org/ui';
import { ClassMultiStepForm } from './class-multi-step-form';
import { Card, CardContent, CardHeader, CardTitle } from '@org/ui';
import { Separator } from '@org/ui';
import { SearchActions } from '@org/ui';
import { Button } from '@org/ui';
import { type FilterDef, type Option } from '@org/ui';
import { Plus, Trash2, ExternalLink, Copy } from 'lucide-react';
import { unwrapTrpcData } from '@org/ui';
import { ClassSelect, LevelSelect } from '@org/model';

type ClassListProps = {
  onCreateClass?: () => void;
  onEditClass?: (classItem: ClassSelect) => void;
  onDeleteClass?: (classItem: ClassSelect) => void;
  onDuplicateClass?: (classItem: ClassSelect) => void;
  p2eBaseUrl?: string;
};

export const ClassList: React.FC<ClassListProps> = ({ onCreateClass, onEditClass, onDeleteClass, onDuplicateClass, p2eBaseUrl }) => {
  const globalWindow =
    typeof globalThis !== 'undefined' && (globalThis as any).window
      ? (globalThis as any).window
      : undefined;
  const { data, isLoading } = trpc.classes.list.useQuery();
  const { data: levelsData } = trpc.levels.list.useQuery();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [filterValue, setFilterValue] = React.useState("");
  const [selectedLevelIds, setSelectedLevelIds] = React.useState<string[]>([]);
  const [selectedClass, setSelectedClass] = React.useState<ClassSelect | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);

  // @ts-ignore
  const classes = React.useMemo<ClassSelect[]>(() => unwrapTrpcData<ClassSelect[]>(data) ?? [], [data]);
  // @ts-ignore
  const levels = React.useMemo<LevelSelect[]>(() => unwrapTrpcData<LevelSelect[]>(levelsData) ?? [], [levelsData]);

  const levelOptions = React.useMemo<Option[]>(() => {
    const levelIds = new Set(
      classes
        .map((item) => item.levelId)
        .filter((value): value is string => Boolean(value))
    );

    const knownOptions = levels
      .filter((level) => level.levelId && levelIds.has(level.levelId))
      .map((level) => ({
        value: level.levelId,
        label: level.name ?? "Unnamed level",
      }));

    const missingOptions = Array.from(levelIds)
      .filter((id) => !knownOptions.some((option) => option.value === id))
      .map((id) => ({
        value: id,
        label: "Unknown level",
      }));

    return [...knownOptions, ...missingOptions];
  }, [classes, levels]);

  const selectClass = (classItem: ClassSelect) => {
    if (onEditClass) {
      onEditClass(classItem);
      return;
    }

    setIsCreating(false);
    setModalOpen(true);
    setSelectedClass(classItem);
  }

  // Function to open form for a new class
  const openNewClassForm = () => {
    if (onCreateClass) {
      onCreateClass();
      return;
    }

    setSelectedClass(null);
    setIsCreating(true);
    setModalOpen(true);
  };

  // Handle filter changes
  const handleFilterChange = (value: string) => {
    setFilterValue(value);
  };

  const levelFilterDefs = React.useMemo<FilterDef[]>(
    () => [{ key: 'level', label: 'Level', type: 'multi', options: levelOptions.map((o) => ({ value: o.value, label: o.label })) }],
    [levelOptions],
  );

  const onClassFormSubmit = () => {
    setModalOpen(false);
  }

  // Filter classes based on search term
  const filteredClasses = React.useMemo(() => {
    if (!classes.length) return [];
    return classes.filter((classItem) => {
      const matchesSearch =
        (classItem.name || "").toLowerCase().includes(filterValue.toLowerCase()) ||
        (classItem.description || "").toLowerCase().includes(filterValue.toLowerCase());
      const matchesLevel =
        !selectedLevelIds.length ||
        (classItem.levelId ? selectedLevelIds.includes(classItem.levelId) : false);

      return matchesSearch && matchesLevel;
    });
  }, [classes, filterValue, selectedLevelIds]);

  // Get image URL - use classItem's imageUrl if available, otherwise generate placeholder
  const getImageUrl = (classItem: ClassSelect) => {
    if (classItem.imageUrl) {
      return classItem.imageUrl;
    }

    const initials = (classItem.name ?? 'Class')
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();

    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=random&color=fff&size=256`;
  };

  return (
    <div>
      <SearchActions 
        onClickAdd={openNewClassForm} 
        onFilterChange={handleFilterChange}
        addButtonText="New Class"
        placeholder="Search classes..."
        filterDefs={levelFilterDefs}
        filterValues={{ level: selectedLevelIds }}
        onFilterValueChange={(key, value) => {
          if (key === 'level') setSelectedLevelIds(Array.isArray(value) ? value : []);
        }}
        onClearFilters={() => setSelectedLevelIds([])}
      />

      <Separator className="my-4" />

      {isLoading ? (
        // Skeleton loader
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={`skeleton-${i}`} className="overflow-hidden max-w-xs mx-auto w-full">
              <div className="flex justify-center p-6 pb-0">
                <div className="w-24 h-24 rounded-full bg-muted animate-pulse" />
              </div>
              <CardHeader className="text-center">
                <div className="h-6 w-2/3 bg-muted animate-pulse rounded mb-2 mx-auto" />
              </CardHeader>
              <CardContent className="text-center">
                <div className="h-4 bg-muted animate-pulse rounded mb-2 w-full" />
                <div className="h-4 bg-muted animate-pulse rounded mb-2 w-5/6 mx-auto" />
                <div className="h-4 bg-muted animate-pulse rounded w-4/6 mx-auto" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {filteredClasses.map((classItem) => (
            <Card 
              key={classItem.classId} 
              className="group relative overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-primary/20 max-w-xs mx-auto w-full"
              onClick={() => selectClass(classItem)}
            >
              <div className="flex justify-center p-6 pb-0">
                <img
                  src={getImageUrl(classItem)}
                  alt={classItem.name || "Class"}
                  className="w-24 h-24 rounded-full object-cover ring-2 ring-muted group-hover:ring-primary/30 transition-all duration-200"
                />
              </div>
              <CardHeader className="text-center">
                <CardTitle as="h2" className="text-base font-semibold leading-tight tracking-tight">{classItem.name || "Unnamed Class"}</CardTitle>
              </CardHeader>
              <CardContent className="text-center pb-3">
                <p className="text-muted-foreground line-clamp-3">
                  {classItem.description || "No description available"}
                </p>
              </CardContent>
              {(p2eBaseUrl || onDuplicateClass || onDeleteClass) && (
                <div className="flex items-center justify-center gap-1 px-4 pb-4 pt-2 border-t border-muted/50">
                  {p2eBaseUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
                      onClick={(event) => {
                        event.stopPropagation();
                        globalWindow?.open?.(`${p2eBaseUrl}/classes/${classItem.classId}`, '_blank');
                      }}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Preview
                    </Button>
                  )}
                  {onDuplicateClass && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDuplicateClass(classItem);
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Duplicate
                    </Button>
                  )}
                  {onDeleteClass && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1.5 text-xs font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDeleteClass(classItem);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </Button>
                  )}
                </div>
              )}
            </Card>
          ))}

          {filteredClasses.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center p-8 text-center">
              <p className="text-muted-foreground mb-4">No classes found{filterValue ? ` matching "${filterValue}"` : ""}</p>
              <Button
                variant="outline"
                onClick={openNewClassForm}
              >
                <Plus className="h-4 w-4" /> Create a Class
              </Button>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
